import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { LogOut, LogIn, QrCode, Search, CheckCircle, AlertCircle, X, Clock, User, Wrench, Camera, CameraOff } from 'lucide-react';
import jsQR from 'jsqr';
import api from '../../services/api';
import { useObras } from './useEstoqueConfig';

function ModalConfirm({ modo, item, funcionario, setFuncionario, funcionarios, observacao, setObservacao, onConfirmar, onCancelar, salvando }) {
  const [buscaFunc, setBuscaFunc] = useState('');

  const funcsFiltrados = funcionarios.filter(f =>
    !buscaFunc || f.nome.toLowerCase().includes(buscaFunc.toLowerCase()) || f.funcao?.toLowerCase().includes(buscaFunc.toLowerCase())
  );

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-end sm:items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md max-h-[90vh] overflow-y-auto">
        <div className={`p-5 rounded-t-2xl ${modo === 'saida' ? 'bg-orange-50 border-b border-orange-100' : 'bg-green-50 border-b border-green-100'}`}>
          <div className="flex items-center justify-between">
            <h2 className={`font-bold text-lg ${modo === 'saida' ? 'text-orange-800' : 'text-green-800'}`}>
              {modo === 'saida' ? 'Confirmar Saída' : 'Confirmar Devolução'}
            </h2>
            <button onClick={onCancelar} className="p-1 rounded-lg hover:bg-black/10">
              <X size={18} />
            </button>
          </div>
        </div>

        <div className="p-5 space-y-4">
          {/* Item */}
          <div className="bg-gray-50 rounded-xl p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                <Wrench size={18} className="text-blue-600" />
              </div>
              <div>
                <p className="font-semibold text-gray-800">{item.nome}</p>
                <p className="text-xs text-gray-500">{item.codigo} {item.marca ? `• ${item.marca}` : ''}</p>
                <p className="text-xs text-gray-400">{item.obra_atual}</p>
              </div>
            </div>
          </div>

          {/* Saída: selecionar funcionário */}
          {modo === 'saida' && (
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Funcionário que está retirando *</label>
              <input
                value={buscaFunc}
                onChange={e => setBuscaFunc(e.target.value)}
                placeholder="Buscar funcionário..."
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm mb-2 focus:outline-none focus:ring-2 focus:ring-orange-400"
              />
              <div className="max-h-40 overflow-y-auto space-y-1 border border-gray-100 rounded-lg p-1">
                {funcsFiltrados.length === 0 && (
                  <p className="text-center text-sm text-gray-400 py-3">Nenhum funcionário encontrado</p>
                )}
                {funcsFiltrados.map(f => (
                  <button key={f.id} onClick={() => setFuncionario(f)}
                    className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors
                      ${funcionario?.id === f.id ? 'bg-orange-100 text-orange-800 font-semibold' : 'hover:bg-gray-50 text-gray-700'}`}>
                    <div className="font-medium">{f.nome}</div>
                    {f.funcao && <div className="text-xs text-gray-400">{f.funcao}</div>}
                  </button>
                ))}
              </div>
              {funcionario && (
                <div className="mt-2 flex items-center gap-2 bg-orange-50 text-orange-800 rounded-lg px-3 py-2 text-sm">
                  <User size={14} /> <strong>{funcionario.nome}</strong> selecionado
                </div>
              )}
            </div>
          )}

          {/* Entrada: mostrar quem tem */}
          {modo === 'entrada' && (
            <div className="bg-blue-50 rounded-xl p-4">
              <p className="text-sm text-blue-700">
                <strong>Responsável atual:</strong> {item.responsavel_atual || '—'}
              </p>
              <p className="text-xs text-blue-500 mt-1">A ferramenta voltará ao status "Disponível" no depósito</p>
            </div>
          )}

          {/* Observação */}
          <div>
            <label className="block text-sm font-medium text-gray-600 mb-1">Observação (opcional)</label>
            <textarea value={observacao} onChange={e => setObservacao(e.target.value)} rows={2}
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
              placeholder="Ex: precisa de manutenção, cuidado com o cabo..." />
          </div>

          {/* Botões */}
          <div className="flex gap-3 pt-1">
            <button onClick={onCancelar}
              className="flex-1 py-3 border border-gray-200 text-gray-600 rounded-xl text-sm font-medium hover:bg-gray-50">
              Cancelar
            </button>
            <button
              onClick={onConfirmar}
              disabled={salvando || (modo === 'saida' && !funcionario)}
              className={`flex-2 flex-1 py-3 text-white rounded-xl text-sm font-bold disabled:opacity-50
                ${modo === 'saida' ? 'bg-orange-500 hover:bg-orange-600' : 'bg-green-500 hover:bg-green-600'}`}>
              {salvando ? 'Registrando...' : modo === 'saida' ? 'Confirmar Saída' : 'Confirmar Devolução'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function EstoqueMovimentacaoDiaria() {
  const qc = useQueryClient();
  const obras = useObras();
  const [modo, setModo] = useState('saida'); // 'saida' | 'entrada'
  const [obraFiltro, setObraFiltro] = useState('');
  const [camera, setCamera] = useState(false);
  const [buscaManual, setBuscaManual] = useState('');
  const [itemEncontrado, setItemEncontrado] = useState(null);
  const [modal, setModal] = useState(false);
  const [funcionario, setFuncionario] = useState(null);
  const [observacao, setObservacao] = useState('');
  const [salvando, setSalvando] = useState(false);
  const [feedbacks, setFeedbacks] = useState([]); // { id, tipo, msg }
  const [scanAtivo, setScanAtivo] = useState(false);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const scanIntervalRef = useRef(null);

  // Funcionários do empreendimento selecionado
  const { data: funcionarios = [] } = useQuery({
    queryKey: ['estoque-funcionarios', obraFiltro],
    queryFn: () => api.get('/estoque/funcionarios', {
      params: { ativo: 'true', obra: obraFiltro || undefined }
    }).then(r => r.data.data),
  });

  // Últimas movimentações do dia
  const { data: pendentes = [], refetch: refetchPendentes } = useQuery({
    queryKey: ['pendentes-devolucao', obraFiltro],
    queryFn: () => api.get('/estoque/movimentacao-diaria/pendentes', {
      params: { obra: obraFiltro || undefined }
    }).then(r => r.data.data),
    refetchInterval: 30000,
  });

  // Histórico recente
  const { data: historico = [], refetch: refetchHistorico } = useQuery({
    queryKey: ['historico-movimentacao', obraFiltro],
    queryFn: () => api.get('/estoque/itens', { params: { obra: obraFiltro || undefined, limit: 1 } }).then(() =>
      api.get('/estoque/movimentacao-diaria/pendentes', { params: { obra: obraFiltro || undefined } }).then(r => r.data.data)
    ).catch(() => []),
    enabled: false,
  });

  const adicionarFeedback = useCallback((tipo, msg) => {
    const id = Date.now();
    setFeedbacks(prev => [{ id, tipo, msg }, ...prev.slice(0, 9)]);
    setTimeout(() => setFeedbacks(prev => prev.filter(f => f.id !== id)), 8000);
  }, []);

  // Camera QR scan
  const iniciarCamera = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'environment' } });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
      }
      setCamera(true);
      setScanAtivo(true);
    } catch (e) {
      adicionarFeedback('erro', 'Não foi possível acessar a câmera');
    }
  }, [adicionarFeedback]);

  const pararCamera = useCallback(() => {
    if (scanIntervalRef.current) clearInterval(scanIntervalRef.current);
    if (streamRef.current) streamRef.current.getTracks().forEach(t => t.stop());
    setCamera(false);
    setScanAtivo(false);
  }, []);

  useEffect(() => () => pararCamera(), [pararCamera]);

  // Loop de leitura QR
  useEffect(() => {
    if (!scanAtivo || !videoRef.current || !canvasRef.current) return;
    const interval = setInterval(() => {
      const video = videoRef.current;
      const canvas = canvasRef.current;
      if (!video || video.readyState !== 4) return;
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(video, 0, 0);
      const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
      const qr = jsQR(imageData.data, imageData.width, imageData.height);
      if (qr?.data) {
        const url = qr.data;
        const tokenMatch = url.match(/token=([a-f0-9-]{36})/);
        if (tokenMatch) {
          clearInterval(interval);
          setScanAtivo(false);
          buscarPorToken(tokenMatch[1]);
        }
      }
    }, 300);
    scanIntervalRef.current = interval;
    return () => clearInterval(interval);
  }, [scanAtivo]);

  async function buscarPorToken(token) {
    try {
      const { data } = await api.get(`/estoque/itens/qr/${token}`);
      if (data.status === 'pregerado') {
        adicionarFeedback('aviso', `QR ${data.numero_etiqueta} ainda não vinculado a nenhuma ferramenta`);
        setScanAtivo(camera);
        return;
      }
      setItemEncontrado(data.data);
      setModal(true);
    } catch {
      adicionarFeedback('erro', 'QR Code não reconhecido no sistema');
      setScanAtivo(camera);
    }
  }

  async function buscarManual() {
    if (!buscaManual.trim()) return;
    try {
      const { data } = await api.get('/estoque/itens', { params: { busca: buscaManual, limit: 10, obra: obraFiltro || undefined } });
      if (!data.data?.length) { adicionarFeedback('aviso', 'Nenhuma ferramenta encontrada'); return; }
      if (data.data.length === 1) {
        setItemEncontrado(data.data[0]);
        setModal(true);
      } else {
        // Mais de um resultado: pegar o primeiro
        setItemEncontrado(data.data[0]);
        setModal(true);
      }
    } catch { adicionarFeedback('erro', 'Erro na busca'); }
  }

  async function confirmar() {
    if (!itemEncontrado) return;
    setSalvando(true);
    try {
      const tipo = modo === 'saida' ? 'saida_funcionario' : 'checkin_funcionario';
      const payload = { tipo, observacao };
      if (modo === 'saida') {
        payload.funcionario_id = funcionario.id;
        payload.responsavel = funcionario.nome;
      } else {
        payload.responsavel = itemEncontrado.responsavel_atual || 'Devolução';
      }
      await api.post(`/estoque/itens/${itemEncontrado.id}/movimentar`, payload);

      const msg = modo === 'saida'
        ? `Saída registrada: ${itemEncontrado.nome} → ${funcionario.nome}`
        : `Devolução registrada: ${itemEncontrado.nome}`;
      adicionarFeedback('sucesso', msg);
      qc.invalidateQueries({ queryKey: ['pendentes-devolucao'] });

      // Reset
      setModal(false);
      setItemEncontrado(null);
      setFuncionario(null);
      setObservacao('');
      setBuscaManual('');
      if (camera) setScanAtivo(true);
    } catch (e) {
      adicionarFeedback('erro', e.response?.data?.error || 'Erro ao registrar movimentação');
    } finally { setSalvando(false); }
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b sticky top-0 z-10 px-4 py-3">
        <div className="max-w-2xl mx-auto flex items-center gap-3 flex-wrap">
          <div className="flex-1 min-w-0">
            <h1 className="text-lg font-bold text-gray-800">Movimentação Diária</h1>
            <p className="text-xs text-gray-500 truncate">Controle de saída e devolução de ferramentas</p>
          </div>
          <select value={obraFiltro} onChange={e => setObraFiltro(e.target.value)}
            className="border border-gray-200 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400">
            <option value="">Todos os depósitos</option>
            {obras.map(o => <option key={o} value={o}>{o}</option>)}
          </select>
        </div>
      </div>

      <div className="max-w-2xl mx-auto p-4 space-y-4">

        {/* Modo Saída / Entrada */}
        <div className="grid grid-cols-2 gap-2 bg-white rounded-xl shadow-sm p-2">
          <button onClick={() => setModo('saida')}
            className={`py-3 rounded-lg text-sm font-bold flex items-center justify-center gap-2 transition-all
              ${modo === 'saida' ? 'bg-orange-500 text-white shadow' : 'text-gray-500 hover:bg-gray-50'}`}>
            <LogOut size={18} /> Saída
          </button>
          <button onClick={() => setModo('entrada')}
            className={`py-3 rounded-lg text-sm font-bold flex items-center justify-center gap-2 transition-all
              ${modo === 'entrada' ? 'bg-green-500 text-white shadow' : 'text-gray-500 hover:bg-gray-50'}`}>
            <LogIn size={18} /> Devolução
          </button>
        </div>

        {/* Feedbacks */}
        {feedbacks.length > 0 && (
          <div className="space-y-2">
            {feedbacks.map(f => (
              <div key={f.id} className={`flex items-start gap-2 rounded-xl px-4 py-3 text-sm animate-fade-in
                ${f.tipo === 'sucesso' ? 'bg-green-50 text-green-800 border border-green-200' :
                  f.tipo === 'erro' ? 'bg-red-50 text-red-800 border border-red-200' :
                  'bg-yellow-50 text-yellow-800 border border-yellow-200'}`}>
                {f.tipo === 'sucesso' ? <CheckCircle size={16} className="mt-0.5 flex-shrink-0" /> :
                 f.tipo === 'erro' ? <AlertCircle size={16} className="mt-0.5 flex-shrink-0" /> :
                 <AlertCircle size={16} className="mt-0.5 flex-shrink-0" />}
                {f.msg}
              </div>
            ))}
          </div>
        )}

        {/* Scanner de câmera */}
        <div className="bg-white rounded-xl shadow-sm overflow-hidden">
          <div className="p-4 flex items-center justify-between border-b">
            <div className="flex items-center gap-2 text-sm font-semibold text-gray-700">
              <QrCode size={18} className="text-blue-500" />
              Escanear QR Code
            </div>
            <button onClick={camera ? pararCamera : iniciarCamera}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors
                ${camera ? 'bg-red-50 text-red-600 hover:bg-red-100' : 'bg-blue-50 text-blue-600 hover:bg-blue-100'}`}>
              {camera ? <><CameraOff size={13} /> Fechar câmera</> : <><Camera size={13} /> Abrir câmera</>}
            </button>
          </div>
          {camera && (
            <div className="relative bg-black">
              <video ref={videoRef} playsInline muted className="w-full max-h-64 object-cover" />
              <canvas ref={canvasRef} className="hidden" />
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                <div className="w-48 h-48 border-2 border-white rounded-xl opacity-60" />
              </div>
              {scanAtivo && (
                <div className="absolute bottom-3 left-0 right-0 text-center text-white text-xs font-medium opacity-80">
                  Aponte para o QR code da ferramenta
                </div>
              )}
              {!scanAtivo && (
                <div className="absolute inset-0 flex items-center justify-center">
                  <button onClick={() => setScanAtivo(true)}
                    className="bg-white text-gray-800 px-4 py-2 rounded-lg text-sm font-semibold shadow">
                    Continuar leitura
                  </button>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Busca manual */}
        <div className="bg-white rounded-xl shadow-sm p-4">
          <p className="text-sm font-semibold text-gray-700 mb-3">Buscar ferramenta manualmente</p>
          <div className="flex gap-2">
            <div className="flex-1 relative">
              <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                value={buscaManual}
                onChange={e => setBuscaManual(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && buscarManual()}
                placeholder="Nome, código ou marca..."
                className="w-full border border-gray-200 rounded-lg pl-9 pr-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
              />
            </div>
            <button onClick={buscarManual}
              className="px-4 py-2.5 bg-blue-600 text-white rounded-lg text-sm font-semibold hover:bg-blue-700">
              Buscar
            </button>
          </div>
        </div>

        {/* Ferramentas pendentes de devolução */}
        {modo === 'entrada' && pendentes.length > 0 && (
          <div className="bg-white rounded-xl shadow-sm overflow-hidden">
            <div className="px-4 py-3 border-b flex items-center justify-between">
              <div className="flex items-center gap-2 text-sm font-semibold text-gray-700">
                <Clock size={16} className="text-orange-500" />
                Ferramentas em campo ({pendentes.length})
              </div>
            </div>
            <div className="divide-y">
              {pendentes.map(p => (
                <div key={p.id} className="px-4 py-3 flex items-center gap-3 hover:bg-gray-50">
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-sm text-gray-800 truncate">{p.item_nome}</div>
                    <div className="text-xs text-gray-500 flex items-center gap-2 mt-0.5">
                      <span className="flex items-center gap-1"><User size={10} /> {p.funcionario_nome}</span>
                      <span className="flex items-center gap-1"><Clock size={10} /> {new Date(p.created_at).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}</span>
                    </div>
                  </div>
                  <button
                    onClick={async () => {
                      const { data } = await api.get(`/estoque/itens/${p.item_id}`);
                      setItemEncontrado(data.data);
                      setModal(true);
                    }}
                    className="px-3 py-1.5 bg-green-50 text-green-700 rounded-lg text-xs font-semibold hover:bg-green-100">
                    Devolver
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {modo === 'entrada' && pendentes.length === 0 && (
          <div className="bg-green-50 border border-green-200 rounded-xl p-4 text-center text-sm text-green-700">
            <CheckCircle size={20} className="mx-auto mb-1 text-green-500" />
            Todas as ferramentas estão no depósito
          </div>
        )}
      </div>

      {/* Modal de confirmação */}
      {modal && itemEncontrado && (
        <ModalConfirm
          modo={modo}
          item={itemEncontrado}
          funcionario={funcionario}
          setFuncionario={setFuncionario}
          funcionarios={funcionarios}
          observacao={observacao}
          setObservacao={setObservacao}
          onConfirmar={confirmar}
          onCancelar={() => {
            setModal(false);
            setItemEncontrado(null);
            setFuncionario(null);
            setObservacao('');
            if (camera) setScanAtivo(true);
          }}
          salvando={salvando}
        />
      )}
    </div>
  );
}
